<template>
	<div class="row maps">
		<div class="columns column3">
			<div class="highlights-navigation">
				<h3>{{ $t('highlightsHeader') }}</h3>
				<div v-for="(category, index) in items" :key="index" class="category">
					<div class="container highlight-category">
						<div
							class="highlight-item button button-toggle"
							:class="{ active: activeCategory === parseInt(category.value, 10) }"
							@click="updateMarkers(parseInt(category.value, 10))"
						>
							<span>{{ category.name }}</span>
						</div>
						<slide-up-down
							:model-value="activeCategory === parseInt(category.value, 10)"
							class="highlight-content"
							:duration="300"
						>
							<ul>
								<li v-for="item in category.items" :key="item.ID">
									<a :data-id="`#huid_${item.ID}`" @click.prevent="openInfowindow($event)">
										{{ item.header }}
									</a>
								</li>
							</ul>
						</slide-up-down>
					</div>
				</div>
			</div>
		</div>
		<div class="columns column9 maps">
			<div id="gmap" ref="gmap" />
		</div>
	</div>
</template>

<script setup>
import { Loader } from '@googlemaps/js-api-loader';
import GoogleMaps from '@becurioushq/googlemaps';

const props = defineProps({
	items: { type: Array, default: () => [] },
});

const config = useRuntimeConfig();
const gmap = ref(null);
const activeCategory = ref(null);

let map = null;
const mapData = {};

const mapConfig = {
	zoom: 11,
	center: { lat: 52.158112, lng: 4.4992507 },
	styles: [
		{ featureType: 'administrative', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{
			featureType: 'landscape',
			elementType: 'all',
			stylers: [{ visibility: 'simplified' }, { hue: '#0066ff' }, { saturation: 74 }, { lightness: 100 }],
		},
		{ featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road', elementType: 'all', stylers: [{ visibility: 'simplified' }] },
		{
			featureType: 'road.highway',
			elementType: 'all',
			stylers: [{ visibility: 'off' }, { weight: 0.6 }, { saturation: -85 }, { lightness: 61 }],
		},
		{ featureType: 'road.highway', elementType: 'geometry', stylers: [{ visibility: 'on' }] },
		{ featureType: 'road.arterial', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road.local', elementType: 'all', stylers: [{ visibility: 'on' }] },
		{ featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'simplified' }] },
		{
			featureType: 'water',
			elementType: 'all',
			stylers: [{ visibility: 'simplified' }, { color: '#5f94ff' }, { lightness: 26 }, { gamma: 5.86 }],
		},
	],
	draggable: true,
	mapTypeControl: false,
	zoomControl: true,
	streetViewControl: true,
	scrollwheel: true,
};

const allHighlights = computed(() => props.items.map((category) => category.items).flat());

const activeHighlights = computed(() =>
	allHighlights.value.filter((item) => {
		return parseInt(item.highlightsCategorie.ID, 10) === parseInt(activeCategory.value, 10);
	}),
);

onMounted(async () => {
	const google = await new Loader({
		apiKey: config.public.maps.apiKey,
	}).load();
	map = new GoogleMaps(google, gmap.value, mapConfig);
	initializeMap();
});

const initializeMap = () => {
	map.setMarkerIcon({
		src: '/images/marker.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 21,
	});

	map.addInfoWindow(
		map.addMarker(Object.values(mapConfig.center), 'Breathe Hotel Leiden'),
		'<h3>Breathe Hotel Leiden</h3>' + '<p class="address">Herengracht 50<br>2312 LE Leiden</p>',
	);
};

const updateMarkers = (id) => {
	if (!map) {
		return;
	}

	if (id === activeCategory.value) {
		activeCategory.value = null;
	} else {
		activeCategory.value = id;
	}

	map.setMarkerIcon({
		src: '/images/marker-item.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 21,
	});

	map.hideMarker('allcategory');

	activeHighlights.value.forEach((highlight) => {
		const marker = map.addMarker(
			[highlight.location.latitude, highlight.location.longitude],
			highlight.header,
			`cat_${highlight.category}`,
		);

		const infowindow = map.addInfoWindow(marker, getInfowindowContent(highlight));

		mapData[`#huid_${highlight.ID}`] = [infowindow, marker];
	});

	if (activeCategory.value) {
		map.autoCenter();
	}
};

const openInfowindow = (event) => {
	const ID = event.target.dataset.id;

	if (ID) {
		const currentMapData = mapData[event.target.dataset.id];
		map.openInfowindow(currentMapData[0], currentMapData[1]);
	}
};

const getInfowindowContent = (highlight) => {
	let html = '<h3>' + highlight.header + '</h3>';

	html += `<p class="address">
			${highlight.location.address}<br>${highlight.location.postalCode} ${highlight.location.city}
			</p>`;

	if (highlight.phonenumber) {
		html += `<p class="phone">${highlight.phonenumber}</p>`;
	}

	if (highlight.distance_public) {
		html += `<p class="directions public"><span class="icon-directions"></span>
				<span>${highlight.distancePublic}</span>
				</p>`;
	}

	if (highlight.distance_walk) {
		html += `<p class="directions walk"><span class="icon-directions"></span>
				<span>${highlight.distanceWalk}</span>
				</p>`;
	}

	if (highlight.url) {
		html += `<p class="website">
				<a href="${highlight.url}" target="_blank" style="outline: none !important;" rel="noopener noreferrer">Website</a>
				</p>`;
	}

	return html;
};
</script>

<style lang="scss" scoped>
.highlights-section {
	.column3 {
		padding: 25px 10px 0;
	}
}

.highlights-navigation {
	padding-right: 0;

	ul {
		margin: 10px 0 20px;
		list-style-position: inside;
		padding: 0 10px;

		li {
			cursor: pointer;
		}
	}
}

.button-toggle {
	padding: 20px;
	transition: all 0.3s ease-in-out;
	width: 100%;
	text-align: left;
	background: #fff;
	color: #d9bd8b;
	cursor: pointer;
	margin: 0 0 4px;
	font-size: 16px;
	font-weight: 300;
	letter-spacing: 0;
	justify-content: flex-start;
	text-transform: none;
	border: 1px solid #d9bd8b;

	&:hover,
	&.active {
		background: #d9bd8b;
		color: #fff;
	}
}

.maps {
	padding: 20px;
	background-color: $body-background-color;
}

#gmap {
	min-height: 600px;
}

@media (max-width: 1080px) {
	.highlights-section .row {
		display: flex;
		flex-flow: column-reverse wrap;
	}

	.highlights-navigation {
		width: 100%;
		padding: 0;
	}

	ul.highlights li {
		width: auto;
		margin: 1% 1% 1% 0;
	}

	.highlights-section {
		.columns {
			width: 100%;
		}
	}
}
</style>
